import {
  Box,
  Column,
  Combobox,
  DollarIcon,
  ExitIcon,
  ExternalLinkIcon,
  FormField,
  NumberInput,
  PercentIcon,
  RadioGroup,
  Row,
  Select,
  Text,
} from "@hightouch/htui";
import pluralize from "pluralize";
import { produce } from "immer";
import { useEffect, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import {
  PredictorOutcomeType,
  type PredictorOutcomeDefinition,
} from "@hightouch/lib/predictive-traits/types";
import { TraitParentModel } from "src/components/audiences/types";
import { RadioCard } from "src/components/radio-card";
import { QueryBuilder } from "src/components/explore";
import { QueryBuilderProvider } from "src/components/explore/context/query-builder-context";
import { ReadOnlyConditions } from "src/components/explore/read-only-conditions";
import { EventFilter } from "src/components/explore/visual/event-filter";
import {
  containsRelativeDateCondition,
  getEventFromPull,
  outcomeFieldsToDefinition,
  OUTCOME_AGGREGATION_LABELS,
  OUTCOME_AGGREGATION_OPTIONS,
  parseOutcomeDefinition,
  PREDICTIVE_ELIGIBILITY_RESTRICTIONS,
  PREDICTIVE_EVENT_FILTER_RESTRICTIONS,
  relativeDateFilterErrorMessage,
} from "src/components/traits/predictive-utils";
import {
  EligbleOutcomeModelsQuery,
  PredictorFragment,
  RelationshipWithoutFilterableColumnsFragment,
  useEligbleOutcomeModelsQuery,
} from "src/graphql";
import { Link } from "src/router";
import {
  ColumnType,
  ConditionType,
  initialEventCondition,
  IntervalUnit,
} from "src/types/visual";
import { PredictorRetrainingWarning } from "src/components/traits/predictor-warnings";

// Valid time units for predictor timeframes (no minutes/seconds allowed)
const PREDICTOR_TIME_OPTIONS = [
  { value: IntervalUnit.Day, label: "Day(s)" },
  { value: IntervalUnit.Week, label: "Week(s)" },
  { value: IntervalUnit.Month, label: "Month(s)" },
  { value: IntervalUnit.Year, label: "Year(s)" },
];

export const predictionUpdateFrequencyOptions = [
  { label: "Every day", value: "daily" },
  { label: "Every 7 days", value: "weekly" },
  { label: "Every 30 days", value: "monthly" },
];

const PREDICTION_MODES = {
  LIKELIHOOD: "likelihood",
  CHURN: "churn",
  VALUE: "value",
} as const;

type Relationship = EligbleOutcomeModelsQuery["model_relationships"][number];

interface PredictiveTraitCalculationProps {
  parentModel: TraitParentModel;
  isCreating: boolean;
}

const maxTextWidth = "576px";

export const PredictiveTraitCalculation = (
  props: PredictiveTraitCalculationProps,
) => {
  const { parentModel, isCreating } = props;

  const features = useMemo(
    () =>
      parentModel.predictor_feature_definitions?.filter((fd) => !fd.catalog_id),
    [parentModel.predictor_feature_definitions],
  );

  const { data: eventRelationships } = useEligbleOutcomeModelsQuery(
    {
      parentModelId: parentModel.id,
    },
    { select: (data) => data.model_relationships },
  );

  // TODO(nolan): use an explicit form state type
  const form = useFormContext();

  const outcomeRelationshipId = form.watch(
    "predictorDefinition.outcome.keyEvent.relationshipId",
  );
  const outcomeType =
    form.watch("predictorDefinition.outcome.type") ??
    PredictorOutcomeType.OCCURRENCE;
  const isValueOutcome = outcomeType === PredictorOutcomeType.VALUE;
  const isChurnOutcome =
    form.watch("predictorDefinition.outcome.timeframe.is_churn") ?? false;
  // The card select is a single choice over (outcome type, is_churn): churn
  // is stored as a flag on the occurrence timeframe, not as an outcome type
  const predictionMode = isValueOutcome
    ? PREDICTION_MODES.VALUE
    : isChurnOutcome
      ? PREDICTION_MODES.CHURN
      : PREDICTION_MODES.LIKELIHOOD;

  const relationshipsById = useMemo(
    () =>
      eventRelationships?.reduce(
        (prev, r) => {
          prev[r.id] = r;
          return prev;
        },
        {} as Record<string, Relationship>,
      ),
    [eventRelationships],
  );

  // Numeric columns of the selected outcome event, for the value-outcome
  // column picker. Mirrors the numeric aggregation column filter used for
  // predictive input events in `pages/schema/predictive/event-filter-list.tsx`.
  const numericColumnOptions = useMemo(() => {
    const relationship = relationshipsById?.[outcomeRelationshipId];
    const columns = relationship?.to_model.filterable_audience_columns ?? [];
    return columns
      .filter(
        (col) =>
          col.type === ColumnType.Number ||
          col.custom_type === ColumnType.Number ||
          col.type === ColumnType.BigInt ||
          col.custom_type === ColumnType.BigInt,
      )
      .map((col) => ({
        label: col.alias || col.name,
        value: col.name,
      }));
  }, [relationshipsById, outcomeRelationshipId]);

  const setValue = form.setValue;
  useEffect(() => {
    if (features && features.length > 0) {
      if (features.length > 1) {
        console.warn("more than one feature definition found for parent model");
      }
      setValue(
        "predictorDefinition.featureDefinitionId",
        features[0]?.id ?? "",
      );
    }
  }, [features, setValue]);

  const isDemo = form.watch("predictorDefinition.config.is_demo");

  if (features && features.length === 0 && !isDemo) {
    return (
      <Column>
        <Text>
          No predictive inputs found for parent model. Please define them for
          this parent model.
        </Text>
        <LinkToFeatureDefinition text="Schema tab" parentModel={parentModel} />
      </Column>
    );
  }

  return (
    <>
      {!isCreating && <PredictorRetrainingWarning />}
      <Column gap={2} mt={2}>
        <Text fontWeight="medium">What action do you want to predict?</Text>
        <Box maxWidth={maxTextWidth}>
          <Text color="text.secondary">
            Pick the event to forecast like a purchase, sign-up, or churn. The
            model predicts the chance it happens within your set timeframe.
          </Text>
        </Box>
        <Column
          borderRadius="md"
          borderWidth="hairline"
          borderStyle="solid"
          borderColor="base.border"
          p={4}
          gap={2}
        >
          {/* TODO(nolan): dedupe same names via relationship id */}
          <Controller
            name="predictorDefinition.outcome.keyEvent.relationshipId"
            render={({ field }) => (
              <Combobox
                value={field.value}
                onChange={(relationshipId) => {
                  field.onChange(relationshipId);
                  // The value column belongs to the previous event's model
                  form.setValue(
                    "predictorDefinition.outcome.valueColumn",
                    undefined,
                  );
                }}
                placeholder="Select event"
                options={
                  eventRelationships?.map((r) => ({
                    label: r.to_model.name,
                    value: r.id.toString(),
                  })) ?? []
                }
              />
            )}
          />

          <QueryBuilderProvider
            restrictions={PREDICTIVE_EVENT_FILTER_RESTRICTIONS}
            parentModel={parentModel}
          >
            <Controller
              name="predictorDefinition.outcome.keyEvent.filter"
              rules={{
                validate: (value) =>
                  containsRelativeDateCondition(value)
                    ? relativeDateFilterErrorMessage
                    : true,
              }}
              render={({ field, fieldState }) =>
                relationshipsById?.[outcomeRelationshipId] ? (
                  <Column p={4} minWidth="xl" width="fit-content" gap={4}>
                    <EventFilter
                      disableChangingEvent
                      hideFunnelCondition
                      hideOperatorFilter
                      hideWindowCondition
                      condition={{
                        ...initialEventCondition,
                        ...field.value,
                        type: ConditionType.Event,
                        eventModelId:
                          relationshipsById[outcomeRelationshipId].to_model.id,
                        relationshipId:
                          relationshipsById[outcomeRelationshipId].id,
                      }}
                      audience={undefined}
                      parent={parentModel}
                      onUpdate={(updater) => {
                        field.onChange(
                          produce(form.getValues(field.name) ?? {}, updater),
                        );
                      }}
                    />
                    {(fieldState.error ||
                      containsRelativeDateCondition(field.value)) && (
                      <Text color="text.danger" size="sm">
                        {relativeDateFilterErrorMessage}
                      </Text>
                    )}
                  </Column>
                ) : (
                  <></>
                )
              }
            />
          </QueryBuilderProvider>
        </Column>
        <Text fontWeight="medium">What kind of prediction is this?</Text>
        <Controller
          name="predictorDefinition.outcome.type"
          defaultValue={PredictorOutcomeType.OCCURRENCE}
          render={({ field }) => (
            <RadioGroup
              width="100%"
              value={predictionMode}
              onChange={(mode) => {
                field.onChange(
                  mode === PREDICTION_MODES.VALUE
                    ? PredictorOutcomeType.VALUE
                    : PredictorOutcomeType.OCCURRENCE,
                );
                const timeframe = form.getValues(
                  "predictorDefinition.outcome.timeframe",
                );
                form.setValue(
                  "predictorDefinition.outcome.timeframe",
                  {
                    quantity: timeframe?.quantity ?? 7,
                    unit: timeframe?.unit ?? IntervalUnit.Day,
                    is_churn: mode === PREDICTION_MODES.CHURN,
                  },
                  { shouldDirty: true },
                );
              }}
            >
              <Row
                gap={3}
                width="100%"
                sx={{
                  "& > label": { flex: 1, w: "auto", h: "auto", minH: "136px" },
                }}
              >
                <RadioCard
                  value={PREDICTION_MODES.LIKELIHOOD}
                  label="Likelihood of event"
                  description="How likely each user is to do this event within the timeframe."
                  icon={<PercentIcon />}
                />
                <RadioCard
                  value={PREDICTION_MODES.CHURN}
                  label="Likelihood of churn"
                  description="How likely each user is to stop doing this event for the timeframe."
                  icon={<ExitIcon />}
                />
                <RadioCard
                  value={PREDICTION_MODES.VALUE}
                  label="Value of event"
                  description="A numeric value from this event, like total revenue over the timeframe."
                  icon={<DollarIcon />}
                />
              </Row>
            </RadioGroup>
          )}
        />
        {isValueOutcome && (
          <Column gap={2}>
            <Box maxWidth={maxTextWidth}>
              <Text color="text.secondary">
                Choose the numeric property of the event to predict and how to
                aggregate it over the timeframe.
              </Text>
            </Box>
            <Row alignItems="center" gap={2}>
              <Controller
                name="predictorDefinition.outcome.aggregation"
                render={({ field }) => (
                  <Select
                    width="auto"
                    placeholder="Select aggregation..."
                    options={OUTCOME_AGGREGATION_OPTIONS}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              <Text>of</Text>
              <Controller
                name="predictorDefinition.outcome.valueColumn"
                render={({ field }) => (
                  <Combobox
                    placeholder="Select numeric property..."
                    options={numericColumnOptions}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </Row>
            {outcomeRelationshipId && numericColumnOptions.length === 0 && (
              <Text color="text.danger" size="sm">
                The selected event has no numeric properties. Choose a different
                event or predict the likelihood of the event instead.
              </Text>
            )}
          </Column>
        )}
        <Controller
          name="predictorDefinition.outcome.timeframe"
          defaultValue={{
            quantity: 7,
            unit: IntervalUnit.Day,
            is_churn: false,
          }}
          render={({ field: timeframeField }) => {
            const isChurnPredictor = timeframeField.value?.is_churn ?? false;

            return (
              <Column gap={3}>
                {/* Timeframe selector - label changes based on prediction kind */}
                <Row alignItems="center" gap={2}>
                  <Text>
                    {isChurnPredictor
                      ? "Churned after no events for"
                      : isValueOutcome
                        ? "Value predicted over the next"
                        : "Event Performed within the next"}
                  </Text>
                  <Box width={16}>
                    <NumberInput
                      placeholder="Enter quantity..."
                      min={1}
                      value={timeframeField.value?.quantity ?? 7}
                      width="100%"
                      onChange={(quantity) => {
                        timeframeField.onChange({
                          quantity,
                          unit: timeframeField.value?.unit ?? IntervalUnit.Day,
                          is_churn: isChurnPredictor,
                        });
                      }}
                    />
                  </Box>
                  <Select
                    width="auto"
                    options={PREDICTOR_TIME_OPTIONS}
                    placeholder="Select unit..."
                    value={timeframeField.value?.unit ?? IntervalUnit.Day}
                    onChange={(unit) => {
                      timeframeField.onChange({
                        quantity: timeframeField.value?.quantity ?? 7,
                        unit,
                        is_churn: isChurnPredictor,
                      });
                    }}
                  />
                </Row>
              </Column>
            );
          }}
        />
      </Column>
      <Controller
        name="predictorDefinition.audienceDefinition"
        render={({ field }) => (
          <FormField label="Who should this prediction apply to?" isOptional>
            <Column gap={2}>
              <Box maxWidth={maxTextWidth}>
                <Text color="text.secondary">
                  Filter to the users you want to score—based on something
                  they’ve already done, like signing up, viewing a page, or
                  starting checkout.
                </Text>
              </Box>
              <Column
                p={4}
                borderWidth="hairline"
                borderStyle="solid"
                borderColor="base.border"
                borderRadius="md"
              >
                <QueryBuilder
                  maxNestingDepth={0}
                  filter={field.value}
                  updateConditions={(updater) =>
                    field.onChange(produce(form.getValues(field.name), updater))
                  }
                  parentModel={parentModel}
                  enableNestedFiltering={false}
                  restrictions={
                    isDemo ? undefined : PREDICTIVE_ELIGIBILITY_RESTRICTIONS
                  }
                />
              </Column>
            </Column>
          </FormField>
        )}
      />
      <Controller
        name="predictorDefinition.config.inferenceCadence"
        render={({ field }) => (
          <FormField label="How often should scores be updated?">
            <Column gap={2}>
              <Box maxWidth={maxTextWidth}>
                <Text color="text.secondary">
                  Set how frequently predictions refresh using the latest data.
                  Faster updates improve freshness but use more resources.
                </Text>
              </Box>
              <Select
                options={predictionUpdateFrequencyOptions}
                value={field.value ?? "weekly"}
                onChange={field.onChange}
              />
            </Column>
          </FormField>
        )}
      />
    </>
  );
};

interface ParentModelForPredictiveTraitCalculation {
  id: string;
  connection: { id: string } | null;
  relationships: RelationshipWithoutFilterableColumnsFragment[];
}

// Frontend-compatible version of PredictorConfig
// This differs from the backend type because the frontend receives data through GraphQL
// as JSONObject, which means strict union types (like "daily" | "weekly" | "monthly")
// become plain strings. All fields are optional since we handle both creating and displaying predictors.
type FrontendPredictorConfig = {
  inferenceCadence?: string;
  is_demo?: boolean;
  demo_data?: {
    outcome?: {
      name: string;
      timeframe: {
        quantity: number;
        unit: string;
        is_churn?: boolean;
      };
      relationship_id?: string;
      filter?: any;
      definition?: unknown;
    };
  };
};

type StoredPredictor = {
  config: FrontendPredictorConfig;
  predictor_outcome: Pick<
    NonNullable<PredictorFragment["predictor_outcome"]>,
    "timeframe" | "cached_event_pull" | "definition"
  > | null;
  audience_definition: PredictorFragment["audience_definition"];
  suppression_filter?: PredictorFragment["suppression_filter"];
  predictor_feature_definition?: {
    id: string;
  } | null;
  catalog_collection_id: string | null;
};

type NewPredictor = {
  config?: FrontendPredictorConfig;
  outcome: {
    keyEvent: {
      name: string;
      // TODO(nolan): improve type
      filter: any;
      relationshipId: string;
    };
    timeframe: {
      quantity: number;
      unit: IntervalUnit;
      is_churn: boolean;
    };
    type?: string;
    valueColumn?: string;
    aggregation?: string;
  };
  featureDefinitionId?: string;
  // TODO(nolan): improve type
  audienceDefinition: any;
  suppression_filter?: PredictorFragment["suppression_filter"];
  catalogId?: string | null;
  catalogDefinition?: any | null;
};

export interface PredictiveTraitCalculationReadonlyProps {
  predictor?: StoredPredictor | NewPredictor | null;
  parentModel?: ParentModelForPredictiveTraitCalculation;
}

function getCalculationData(predictor: StoredPredictor | NewPredictor): {
  outcomeEvent:
    | { name?: string; relationshipId?: string; filter?: any }
    | undefined;
  audienceDefinition: any;
  timeframe:
    | { quantity: number; unit: IntervalUnit | string; is_churn?: boolean }
    | undefined;
  outcomeDefinition: PredictorOutcomeDefinition | null;
} {
  // Case 1: NewPredictor - creating a new predictor (not yet saved)
  if ("outcome" in predictor && "audienceDefinition" in predictor) {
    return {
      outcomeEvent: predictor.outcome.keyEvent,
      audienceDefinition: predictor.audienceDefinition,
      timeframe: predictor.outcome.timeframe,
      outcomeDefinition: outcomeFieldsToDefinition(predictor.outcome),
    };
  }

  // Case 2: Demo StoredPredictor - demo predictor from database
  // Demo predictors store outcome data in config.demo_data instead of predictor_outcome table
  if (predictor.config?.is_demo && predictor.config.demo_data?.outcome) {
    return {
      outcomeEvent: {
        name: predictor.config.demo_data.outcome.name,
        relationshipId: predictor.config.demo_data.outcome.relationship_id,
        filter: predictor.config.demo_data.outcome.filter,
      },
      audienceDefinition: predictor.audience_definition,
      timeframe: {
        quantity: predictor.config.demo_data.outcome.timeframe.quantity,
        unit: predictor.config.demo_data.outcome.timeframe.unit as IntervalUnit,
        is_churn:
          predictor.config.demo_data.outcome.timeframe.is_churn ?? false,
      },
      outcomeDefinition: parseOutcomeDefinition(
        predictor.config.demo_data.outcome.definition,
      ),
    };
  }

  // Case 3: Normal StoredPredictor - regular predictor from database
  return {
    outcomeEvent: getEventFromPull(
      predictor.predictor_outcome?.cached_event_pull,
    ),
    audienceDefinition: predictor.audience_definition,
    timeframe: predictor.predictor_outcome?.timeframe,
    outcomeDefinition: parseOutcomeDefinition(
      predictor.predictor_outcome?.definition,
    ),
  };
}

export const PredictiveTraitCalculationReadonly = (
  props: PredictiveTraitCalculationReadonlyProps,
) => {
  const { predictor, parentModel } = props;
  if (!predictor || !parentModel) {
    // TODO(nolan)
    return <Text>There was an issue retrieving the predictor.</Text>;
  }

  const { outcomeEvent, audienceDefinition, timeframe, outcomeDefinition } =
    getCalculationData(predictor);
  const valueOutcome =
    outcomeDefinition?.type === PredictorOutcomeType.VALUE
      ? outcomeDefinition
      : null;

  const relationship = outcomeEvent?.relationshipId
    ? parentModel.relationships.find(
        (r) => r.id && r.id.toString() === outcomeEvent?.relationshipId,
      )
    : undefined;

  return (
    <Column gap={6} bg="base.background" borderRadius="md" py={3} px={4}>
      <Column gap={2}>
        <Text fontWeight="medium">
          {timeframe?.is_churn
            ? "Churn prediction"
            : valueOutcome
              ? "Predicted outcome value"
              : "Predicted outcome event"}
        </Text>
        {valueOutcome && (
          <Text>
            {OUTCOME_AGGREGATION_LABELS[valueOutcome.aggregation]} of{" "}
            <Text as="span" fontWeight="medium">
              {valueOutcome.value_column}
            </Text>{" "}
            from
          </Text>
        )}
        <ReadOnlyConditions
          noBackgroundColor
          rootConditions={
            outcomeEvent?.filter?.type
              ? [outcomeEvent?.filter]
              : [
                  {
                    ...initialEventCondition,
                    relationshipId: outcomeEvent?.relationshipId,
                    eventModelId: relationship?.to_model.id,
                  },
                ]
          }
          audiences={[]}
          traits={[]}
          relationships={parentModel.relationships}
          journeys={[]}
        />
        {timeframe && (
          <Text>
            {timeframe.is_churn ? "Inactive for " : "Within "}
            {pluralize(timeframe.unit, timeframe.quantity, true)}
          </Text>
        )}
      </Column>
      <Column gap={2}>
        <Text fontWeight="medium">Eligible users</Text>
        {audienceDefinition?.conditions &&
        audienceDefinition?.conditions.length > 0 ? (
          <ReadOnlyConditions
            rootConditions={[audienceDefinition]}
            audiences={[]}
            traits={[]}
            relationships={parentModel.relationships}
            journeys={[]}
          />
        ) : (
          <Text>No filters</Text>
        )}
      </Column>
      <LinkToFeatureDefinition parentModel={parentModel} />
      <Column gap={2}>
        <Text fontWeight="medium">How often should scores be updated?</Text>
        <Text>
          {
            predictionUpdateFrequencyOptions.find(
              (option) =>
                option.value ===
                (predictor.config?.inferenceCadence ?? "weekly"),
            )?.label
          }
        </Text>
      </Column>
    </Column>
  );
};

const LinkToFeatureDefinition = ({
  parentModel,
  text = "Configure Predictive Inputs",
}: {
  parentModel: ParentModelForPredictiveTraitCalculation;
  text?: string;
}) => (
  <Link
    href={`/schema-v2/view/predictive?source=${parentModel.connection?.id}&id=${parentModel.id}`}
    isExternal
  >
    {text} <ExternalLinkIcon />
  </Link>
);
